import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PortalModule } from '@angular/cdk/portal';
import { OneFooterService } from './one-footer.service';
import { environment } from '../../../../environments/environment';
import { getDefaultLogo } from '../../utils/logo.util';
import { AppContextService } from '../../services/app-context.service';

@Component({
  selector: 'one-footer',
  standalone: true,
  imports: [CommonModule, PortalModule],
  templateUrl: './one-footer.component.html',
  styleUrl: './one-footer.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneFooterComponent {
  readonly footer = inject(OneFooterService);
  readonly marcaDagua = getDefaultLogo();
  readonly siteUrl = environment.urlSite;
  /** No admin, o "Powered by" global do app.component já aparece (showFooter && !showMenu) — evita duplicar aqui. */
  readonly ocultarMarcaDagua = inject(AppContextService).isAdmin;

  /** true enquanto a barra está "grudada" no rodapé (conteúdo rola atrás dela). */
  readonly isStuck = signal(false);

  private readonly sentinel = viewChild<ElementRef<HTMLElement>>('sentinel');

  private readonly cdr = inject(ChangeDetectorRef);

  constructor() {
    const destroyRef = inject(DestroyRef);

    // Permite que uma página force o re-check síncrono dos botões projetados (portal CDK),
    // quando o gatilho é um signal local (fora da árvore de CD do footer). Ver OneFooterService.refresh().
    this.footer.registerRefresh(() => this.cdr.detectChanges());

    // afterNextRender só roda no browser (SSR-safe) e após o sentinela existir.
    afterNextRender(() => {
      const el = this.sentinel()?.nativeElement;
      if (!el || typeof IntersectionObserver === 'undefined') {
        return;
      }

      // Sentinela visível no viewport = barra em repouso (fim da página à vista);
      // fora do viewport = barra grudada no rodapé. O +2px no rodapé garante que,
      // rolado até o fim, o sentinela conte como visível (evita falso "grudado").
      const observer = new IntersectionObserver(
        ([entry]) => this.isStuck.set(!entry.isIntersecting),
        { root: null, threshold: 0, rootMargin: '0px 0px 2px 0px' },
      );
      observer.observe(el);
      destroyRef.onDestroy(() => observer.disconnect());
    });
  }
}
